/**
 * ChannelLineChart · Module B - 折线图（按时间维度）
 *
 * 需求 §4.4.5：
 * - 每条线代表一款游戏
 * - X 轴日期，Y 轴当前选中指标的数值
 * - 折线图时按选定的 channel_id 数据展示（'all' 即所有渠道加总）
 * - 配色 chartAllColors（EChartCommon 默认）
 * - tooltip / legend 复用 TrendCompare/utils（带游戏 icon）
 */
import React, { memo, useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_EC, DBO_V } from '@op/utils/format';
import { TMetric } from '../../types/common';
import { IFilter } from '../../types/filter';
import { TGameMap } from '../../hooks/useGameMap';
import { useGameColorContext } from '../../context/GameColorContext';
import { METRIC_METAS } from '../../constants/metrics';
import { useChannelTimeline } from '../../hooks/useChannelCompare';
import {
  buildEchartSeries,
  buildGameRich,
  buildXAxisData,
  gameRichLabel,
  gameTooltipLabel,
} from '../TrendCompare/utils';

export interface IChannelLineChartProps {
  filter: IFilter;
  channelIds: string[];
  gameMap: TGameMap;
  metric: TMetric;
  height?: number;
  /** 触发 dataZoom 的窗口大小 */
  dataZoomThreshold?: number;
  emptyTitleText?: string; 
}

const DEFAULT_DATA_ZOOM_THRESHOLD = 5;

/**
 * ⚠️ 数据获取内置：组件内部调用 `useChannelTimeline`（接口 timeline）。
 * 在 ChannelCompare 里仅当 chartType === 'line' 时挂载本组件 → 切到 line 才发请求；
 * 切走时组件卸载 + `useCancelRequestHook` 自动取消 in-flight 请求。
 */
const ChannelLineChart = memo(({
  filter,
  channelIds,
  gameMap,
  metric,
  height = 360,
  dataZoomThreshold = DEFAULT_DATA_ZOOM_THRESHOLD,
  emptyTitleText, 
}: IChannelLineChartProps) => {
  const { series, loading } = useChannelTimeline({ filter, channelIds, metric });
  const dateType = filter.date_type;
  const { gameColorMap } = useGameColorContext();

  const empty = useMemo(() => {
    return !series?.length || series.every((item) => !item.values?.length);
  }, [series]);

  const options = useMemo(() => {
    if(empty) return {};
    const xAxisData = buildXAxisData(series);
    const echartSeries = buildEchartSeries(series, 'line', gameColorMap);
    const gameRich = buildGameRich(series, gameMap, 16);
    const isMoreData = false; // xAxisData.length > dataZoomThreshold;

    const yFormatter = METRIC_METAS[metric]?.yFormatter;
    const valueFormatter =
      yFormatter === 'percent'
        ? (val: number) =>
          Number.isFinite(val) ? DBO_V.percentageFormat(val * 100, '-', 0) : '-'
        : (val: number) => (Number.isFinite(val) ? DBO_V.reviews(val) : '-');

    // 图例顺序：按第一个时间点的游戏值从大到小排列
    const firstTimeValues = new Map<string, number>();
    series.forEach((s) => {
      const firstValue = s.values?.[0]?.value;
      if (firstValue !== undefined) {
        firstTimeValues.set(s.game_id, firstValue);
      }
    });
    const legendData = series
      .map((s) => s.game_id)
      .sort((a, b) => (firstTimeValues.get(b) || 0) - (firstTimeValues.get(a) || 0));

    const baseOptions: any = {
      legend: {
        data: legendData,
        formatter: (gameId: string) => gameRichLabel(gameId, gameMap),
        textStyle: { rich: gameRich },
      },
      tooltip: {
        formatter: (params: any) =>
          formatChartParamsDate({
            title: DBO_EC.toolTime(params?.[0]?.axisValue, dateType),
            params,
            isSort: true,
            listTitleFunc: (item: any) =>
              gameTooltipLabel(item.seriesName, gameMap),
            valueFormat: valueFormatter,
          }),
      },
      xAxis: {
        data: xAxisData,
        axisLabel: {
          formatter: (value: string) => DBO_EC.timeX(value, dateType),
        },
      },
      yAxis: {
        axisLabel: {
          formatter: (value: number) =>
            yFormatter === 'percent' ? DBO_EC.percent(value, false) : DBO_EC.reviews(value),
        },
      },
      axisPointer: {
        label: {
          formatter: ({ value }: { value: number | string; }) => {
            if (dateType === 'monthly') {
              return DBO_EC.timeX(String(value), dateType);
            }
            return value;
          },
        }
      },
      series: echartSeries,
    };

    if (isMoreData) {
      const startIdx = Math.max(0, xAxisData.length - dataZoomThreshold);
      const endIdx = xAxisData.length - 1;
      return {
        ...baseOptions,
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: 0,
            filterMode: 'none',
            startValue: startIdx,
            endValue: endIdx,
            zoomOnMouseWheel: false,
            moveOnMouseWheel: true,
            moveOnMouseMove: true,
          },
          {
            type: 'slider',
            xAxisIndex: 0,
            filterMode: 'none',
            startValue: startIdx,
            endValue: endIdx,
            height: 16,
            bottom: 40,
            showDetail: false,
            showDataShadow: false,
            borderColor: 'rgba(0,0,0,0)',
            backgroundColor: 'rgba(0,0,0,0.02)',
            fillerColor: 'rgba(80, 134, 243, 0.2)',
            handleSize: '100%',
            handleStyle: { color: '#D8DAE4', borderCap: 'square' },
            moveHandleStyle: { color: '#D8DAE4' },
            emphasis: {
              handleStyle: { color: '#5086F3' },
              moveHandleStyle: { color: '#5086F3' },
            },
          },
        ],
        grid: { bottom: 70, containLabel: true }
      };
    }

    return baseOptions;
  }, [series, gameMap, dateType, metric, gameColorMap]);

  return (
    <EChartCommon
      options={options}
      optionsProps={{ type: 'line' }}
      height={height}
      loading={loading}
      empty={empty}
      emptyTitleText={emptyTitleText}
      hasEmptyDesc={false} 
    />
  );
});

ChannelLineChart.displayName = 'ChannelLineChart';

export default ChannelLineChart;
